import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Upload, Download, FileCheck } from "lucide-react";
import { DQChecklistSection } from "./DQChecklistSection";
import { driverApi, documentApi, auditApi } from "@/lib/entities";
import { apiError } from "@/lib/errors";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { SectionHeader } from "@/components/SectionHeader";
import { DemoChip, CredentialChip } from "@/components/Chips";
import { RoleGate } from "@/components/RoleGate";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DriverFormDialog } from "@/pages/drivers/DriverFormDialog";
import DriverOperationsTab from "@/pages/drivers/tabs/DriverOperationsTab";
import DriverAvailabilityTab from "@/pages/drivers/tabs/DriverAvailabilityTab";
import DriverClientEligibilityTab from "@/pages/drivers/tabs/DriverClientEligibilityTab";
import { ArchiveConfirmModal, useArchive } from "@/components/ActionMenu";

// PRG · Wave 4B-6 canonical resolver. `can("drivers.edit")` auto-grants
// Standard Admin (role_class.is_admin=True OR role=="admin") the same
// write capability that Super Admin and ops_dispatch have on Driver
// records. Backend `drivers.py` READ/WRITE guards already include
// `"admin"`.
const READ_ROLES = ["super_admin", "admin", "leasing_sales", "ops_dispatch", "accounting", "maintenance"];

// F3 · Driver employment status badge palette. Mirrors the coach-blue
// / amber-hold / critical-red token set already used elsewhere.
const STATUS_BADGE = {
  active:     { label: "Active",     cls: "bg-coach-blue/10 text-coach-blue border-coach-blue/30" },
  on_hold:    { label: "On Hold",    cls: "bg-amber-hold/15 text-amber-hold border-amber-hold/40" },
  quit:       { label: "Quit",       cls: "bg-steel/10 text-steel border-steel/40" },
  terminated: { label: "Terminated", cls: "bg-critical-red/10 text-critical-red border-critical-red/30" },
};

// Curated document types most useful on a Driver record. `passport` is
// F3-new. `other` is a catch-all for arbitrary attachments.
const DOC_TYPE_OPTIONS = [
  ["cdl",          "CDL"],
  ["medical_card", "Medical Card"],
  ["mvr",          "MVR"],
  ["passport",     "Passport"],
  ["w9",           "W-9"],
  ["coi",          "Certificate of Insurance"],
  ["insurance",    "Insurance"],
  ["other",        "Other"],
];

export default function DriverDetail() {
  const { id } = useParams();
  const { user, can } = useAuth();
  const nav = useNavigate();
  const [driver, setDriver] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const canWrite = can("drivers.edit");
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";

  const load = async () => {
    try {
      const d = await driverApi.get(id);
      setDriver(d);
    } catch (e) {
      toast.error("Driver not found");
    }
  };

  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [id]);

  if (!driver) {
    return (
      <PageShell>
        {/* RC4 · shared SectionHeader adoption landmark (sr-only until full visual migration lands progressively). */}
        <SectionHeader testid="page-section-landmark" className="sr-only">Driver Detail</SectionHeader>
        <div className="text-steel text-sm">Loading driver…</div>
      </PageShell>
    );
  }

  const status = STATUS_BADGE[driver.employment_status] || STATUS_BADGE.active;

  return (
    <RoleGate allow={READ_ROLES}>
      <PageShell>
        <PageHeader
          testid="page-driver-detail"
          backTo={{ label: "Back to Drivers", to: "/drivers" }}
          title={
            <span className="inline-flex items-center gap-3">
              {driver.display_name} {driver.is_demo && <DemoChip />}
              <span
                data-testid="driver-status-badge"
                className={`text-[10.5px] uppercase tracking-widest border rounded-sm px-2 py-0.5 ${status.cls}`}
              >
                {status.label}
              </span>
            </span>
          }
          subtitle={
            // RC4 · ITEM 8 — driver_number hidden. Show role/status
            // context in the subtitle slot when useful (kept empty
            // here as it appears in the badge chip above).
            null
          }
          right={
            canWrite && activeTab !== "operations" && (
              <button
                data-testid="driver-edit-btn"
                onClick={() => setEditOpen(true)}
                className="px-3 py-1.5 text-[12.5px] border border-coach-blue text-coach-blue rounded-sm hover:bg-coach-blue hover:text-white"
              >
                Edit Driver
              </button>
            )
          }
        />

        <Tabs defaultValue={activeTab} className="mt-2">
          <TabsList data-testid="driver-tabs" className="bg-white border border-coach-silver rounded-sm w-full justify-start">
            {[
              ["overview", "Overview"],
              ["compliance", "Compliance"],
              ["availability", "Availability"],
              ["client-eligibility", "Client Eligibility"],
              ["operations", "Driver Operations"],
              ["documents", "Documents"],
              ["activity", "Activity"],
            ].map(([k, l]) => (
              <TabsTrigger key={k} value={k} data-testid={`driver-tab-${k}`} className="data-[state=active]:bg-coach-blue data-[state=active]:text-white">
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <div className="bg-white border border-coach-silver rounded-md p-4 grid md:grid-cols-2 gap-4 text-[13px]">
              <Row k="Phone" v={driver.phone_primary || "—"} />
              <Row k="Email" v={driver.email || "—"} />
              <Row k="Employment Type" v={driver.employment_type || "—"} />
              <Row k="Employment Status" v={status.label} />
              <Row k="Hire Date" v={driver.hire_date || "—"} />
              {/* RC10j-10-14 · Derived Assigned Unit (reads from
                  Asset.primary_driver_id — no mirrored field on Driver).
                  Shows [CONFLICT] label if multiple assets reference
                  this driver as primary (should be impossible due to
                  server-side uniqueness). */}
              <Row
                k="Assigned Unit"
                v={
                  <span data-testid="driver-assigned-unit">
                    {!driver.assigned_unit?.asset_id && (
                      <span className="text-steel">Not assigned</span>
                    )}
                    {driver.assigned_unit?.asset_id && !driver.assigned_unit?.conflict && (
                      <Link
                        data-testid="driver-assigned-unit-link"
                        to={`/fleet/${driver.assigned_unit.asset_id}`}
                        className="text-coach-blue hover:underline"
                      >
                        {driver.assigned_unit.unit_name}
                      </Link>
                    )}
                    {driver.assigned_unit?.conflict && (
                      <span className="text-maintenance-orange">
                        [CONFLICT — multiple assignments]
                      </span>
                    )}
                  </span>
                }
              />
              {/* RC10j — Date of Birth. Authorized roles see MM/DD/YYYY
                  (or "Not entered" for null). Unauthorized roles see
                  "Restricted" — matches the passport-info pattern used
                  elsewhere on this profile. */}
              <Row
                k="Date of Birth"
                v={
                  <span data-testid="driver-dob">
                    {driver.dob_access === "restricted" ? (
                      <span className="text-steel">Restricted</span>
                    ) : driver.date_of_birth ? (
                      _formatDobMMDDYYYY(driver.date_of_birth)
                    ) : (
                      <span className="text-steel">Not entered</span>
                    )}
                  </span>
                }
              />
              {/* RC10b Item 1 — Driver-level `mvr_status` and
                  `drug_alcohol_program_status` are DEPRECATED and no
                  longer rendered. Status is derived from the DQ
                  Checklist via `driver.dq_derived` (populated by the
                  Driver API response). */}
              <Row k="MVR" v={<DqStatusPill s={driver.dq_derived?.mvr} testid="driver-dq-mvr-status" />} />
              <Row k="Drug/Alcohol Program" v={<DqStatusPill s={driver.dq_derived?.drug_alcohol} testid="driver-dq-da-status" />} />
            </div>
            {(user?.role === "super_admin" || user?.role === "admin") && (
              <div className="mt-3 text-[12px]">
                <a
                  href={`/drivers/${id}?tab=compliance`}
                  data-testid="driver-manage-dq-checklist-link"
                  className="text-coach-blue hover:underline"
                >
                  Manage DQ Checklist →
                </a>
              </div>
            )}
          </TabsContent>

          <TabsContent value="compliance" className="mt-4">
            <div className="bg-white border border-coach-silver rounded-md p-4 mb-4">
              {/* RC4 Part 2 · ITEM 4 — Passport section (super_admin/
                  admin see the raw number; other roles receive `null`
                  from the API and see "Restricted" here). */}
              <SectionHeader testid="driver-section-passport">Passport</SectionHeader>
              <div className="grid md:grid-cols-4 gap-3 text-[12.5px]">
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-steel">Passport held</div>
                  <div className="mt-1 text-carbon" data-testid="driver-passport-held">{driver.passport_held ? "Yes" : "No"}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-steel">Number</div>
                  <div className="mt-1 text-carbon font-mono" data-testid="driver-passport-number">
                    {driver.passport_held
                      ? (driver.passport_number || <span className="text-steel">Restricted</span>)
                      : <span className="text-steel">—</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-steel">Issuing country</div>
                  <div className="mt-1 text-carbon">{driver.passport_issuing_country || <span className="text-steel">—</span>}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-steel">Status</div>
                  <div className="mt-1 text-carbon" data-testid="driver-passport-status">
                    <PassportStatusBadge status={driver.passport_status} exp={driver.passport_expiration} />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-coach-silver rounded-md p-4 mb-4">
              {/* RC4 Part 2 · ITEM 4 — Employment & Insurance section.
                  Insurance rates are intentionally not captured — only
                  classification + payment metadata (rates are future
                  configurable business data). */}
              <SectionHeader testid="driver-section-employment">Employment & Insurance</SectionHeader>
              <div className="grid md:grid-cols-4 gap-3 text-[12.5px]">
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-steel">Classification</div>
                  <div className="mt-1 text-carbon" data-testid="driver-classification">
                    {driver.driver_classification === "full_time_driver" ? "Full-time driver"
                      : driver.driver_classification === "co_driver" ? "Co-driver"
                      : <span className="text-steel">—</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-steel">Annual premium paid</div>
                  <div className="mt-1 text-carbon">{driver.annual_insurance_premium_paid ? "Yes" : "No"}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-steel">Coverage year</div>
                  <div className="mt-1 text-carbon">{driver.insurance_coverage_year || <span className="text-steel">—</span>}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-steel">Payment date</div>
                  <div className="mt-1 text-carbon">{driver.insurance_payment_date || <span className="text-steel">—</span>}</div>
                </div>
              </div>
              {driver.insurance_notes && (
                <div className="mt-3">
                  <div className="text-[10.5px] uppercase tracking-widest text-steel">Notes</div>
                  <div className="mt-1 text-carbon text-[12.5px] whitespace-pre-wrap">{driver.insurance_notes}</div>
                </div>
              )}
            </div>

            {/* RC6 · Administrative DQ File Checklist. Renders only
                for super_admin / admin; server additionally returns
                403 for any other role. Positioned directly below
                Employment & Insurance per Owner directive. */}
            {(user?.role === "super_admin" || user?.role === "admin") && (
              <DQChecklistSection driverId={id} canEdit={true} />
            )}

            {/* RC5 Item 4 — DQ File (Driver Qualification File). Positioned
                directly below the Employer / Employment section per
                Owner directive. Reuses existing document types + credential
                status; no new backend enum values, no FMCSA API call. */}
            <DQFileSection driver={driver} onOpenDocuments={() => nav(`/drivers/${id}?tab=documents`)} />

            <div className="bg-white border border-coach-silver rounded-md p-4 grid md:grid-cols-3 gap-4">
              <ComplianceItem label="CDL" exp={driver.cdl_expiration} level={driver.credential_status?.cdl} extra={`${driver.cdl_class || ""} · ${driver.cdl_state || ""}`} />
              <ComplianceItem label="Medical Card" exp={driver.medical_card_expiration} level={driver.credential_status?.medical_card} />
              <ComplianceItem label="Passport" exp={driver.passport_expiration} level={driver.credential_status?.passport} extra={driver.passport_number ? `#${driver.passport_number}` : ""} />
            </div>
          </TabsContent>

          <TabsContent value="availability" className="mt-4">
            <DriverAvailabilityTab driverId={id} />
          </TabsContent>

          <TabsContent value="client-eligibility" className="mt-4">
            <DriverClientEligibilityTab driverId={id} />
          </TabsContent>

          <TabsContent value="documents" className="mt-4">
            <DriverDocumentsPanel driverId={id} canWrite={canWrite} typeOptions={DOC_TYPE_OPTIONS} />
          </TabsContent>

          <TabsContent value="operations" className="mt-4">
            <DriverOperationsTab driverId={id} />
          </TabsContent>

          <TabsContent value="activity" className="mt-4">
            <DriverActivityPanel driverId={id} />
          </TabsContent>
        </Tabs>

        {editOpen && (
          <DriverFormDialog
            open={editOpen}
            initial={driver}
            onClose={() => setEditOpen(false)}
            onSaved={() => {
              setEditOpen(false);
              load();
            }}
          />
        )}
      </PageShell>
    </RoleGate>
  );
}

/* ── Documents panel ──────────────────────────────────────────────── */

function DriverDocumentsPanel({ driverId, canWrite, typeOptions }) {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("cdl");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const rows = await documentApi.list({
        entity_type: "driver",
        entity_id: driverId,
        limit: 200,
      });
      // Hide archived, most recent first
      const active = (rows || [])
        .filter((r) => !r.is_archived)
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setDocs(active);
    } catch (e) {
      toast.error("Failed to load documents");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [driverId]);

  const archive = useArchive({
    entityLabel: "Document",
    apiArchive: (docId) => documentApi.archive(docId),
    onSuccess: async () => { toast.success("Document archived"); await load(); },
  });

  const handleUpload = async (file) => {
    if (!file) return;
    if (!title.trim()) {
      toast.error("Add a title for the document");
      return;
    }
    setUploading(true);
    try {
      await documentApi.upload({
        file,
        title: title.trim(),
        document_type: docType,
        entity_type: "driver",
        entity_id: driverId,
        notes: notes || undefined,
      });
      toast.success("Document uploaded");
      setTitle("");
      setNotes("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const download = async (doc) => {
    try {
      const url = await documentApi.signedUrl(doc.id);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="driver-documents-panel">
      {canWrite && (
        <div className="bg-white border border-coach-silver rounded-md p-4">
          <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-2">
            Upload Document
          </div>
          <div className="grid md:grid-cols-4 gap-2 text-[13px]">
            <label className="block md:col-span-2">
              <div className="text-[11px] text-steel">Title</div>
              <input
                data-testid="doc-upload-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. CDL front"
                className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
              />
            </label>
            <label className="block">
              <div className="text-[11px] text-steel">Type</div>
              <select
                data-testid="doc-upload-type"
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white"
              >
                {typeOptions.map(([k, l]) => (
                  <option key={k} value={k}>{l}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <div className="text-[11px] text-steel">Notes (optional)</div>
              <input
                data-testid="doc-upload-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
              />
            </label>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              data-testid="doc-upload-file"
              accept="application/pdf,image/jpeg,image/png"
              disabled={uploading}
              onChange={(e) => handleUpload(e.target.files?.[0])}
              className="text-[12px]"
            />
            {uploading && <span className="text-[12px] text-steel">Uploading…</span>}
          </div>
        </div>
      )}

      <div className="bg-white border border-coach-silver rounded-md">
        {docs === null ? (
          <div className="p-6 text-center text-steel text-[13px]">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="p-6 text-center text-steel text-[13px]" data-testid="driver-documents-empty">
            No driver documents on file yet.
          </div>
        ) : (
          <table className="w-full text-[13px]" data-testid="driver-documents-table">
            <thead className="text-[10.5px] uppercase tracking-widest text-steel">
              <tr className="border-b border-coach-silver/60">
                <th className="text-left px-4 py-2">Title</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Uploaded</th>
                <th className="px-4 py-2 w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => {
                const typeLabel = typeOptions.find(([k]) => k === d.document_type)?.[1] || d.document_type;
                return (
                  <tr key={d.id} data-testid={`doc-row-${d.id}`}
                      className="border-b border-coach-silver/60 last:border-0 hover:bg-road-white">
                    <td className="px-4 py-2">
                      <div className="text-carbon font-medium">{d.title}</div>
                      {d.notes && <div className="text-[11.5px] text-steel">{d.notes}</div>}
                    </td>
                    <td className="px-4 py-2 text-steel">{typeLabel}</td>
                    <td className="px-4 py-2 text-steel hidden md:table-cell">
                      {d.created_at ? d.created_at.slice(0, 10) : "—"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        data-testid={`doc-download-${d.id}`}
                        onClick={() => download(d)}
                        className="inline-flex items-center gap-1 text-[12px] text-coach-blue hover:underline mr-3"
                      >
                        <Download size={12} /> Download
                      </button>
                      {canWrite && (
                        <button
                          data-testid={`doc-archive-${d.id}`}
                          onClick={() => archive.request(d)}
                          className="inline-flex items-center gap-1 text-[12px] text-critical-red hover:underline"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <ArchiveConfirmModal {...archive.modalProps} />
    </div>
  );
}

/* ── Activity panel ──────────────────────────────────────────────── */

function DriverActivityPanel({ driverId }) {
  const [state, setState] = useState({ loading: true, denied: false, entries: [] });

  useEffect(() => {
    (async () => {
      try {
        const entries = await auditApi.forEntity("driver", driverId, 100);
        setState({ loading: false, denied: false, entries: entries || [] });
      } catch (e) {
        const code = e?.response?.status;
        if (code === 401 || code === 403) {
          setState({ loading: false, denied: true, entries: [] });
        } else {
          setState({ loading: false, denied: false, entries: [] });
          toast.error("Failed to load activity");
        }
      }
    })();
  }, [driverId]);

  if (state.loading) return <div className="text-[12px] text-steel">Loading activity…</div>;
  if (state.denied) return <div className="text-[12px] text-steel" data-testid="driver-activity-denied">Activity log is not available for your role.</div>;
  if (state.entries.length === 0) {
    return <div className="text-[12px] text-steel" data-testid="driver-activity-empty">No activity recorded yet.</div>;
  }

  return (
    <div className="bg-white border border-coach-silver rounded-md" data-testid="driver-activity-list">
      <ul className="divide-y divide-coach-silver/60">
        {state.entries.map((e) => (
          <li key={e.id} className="px-4 py-2.5 text-[13px]">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[11.5px] text-carbon">{e.action}</div>
              <div className="text-[11px] text-steel">
                {e.ts ? e.ts.replace("T", " ").slice(0, 19) : ""}
              </div>
            </div>
            <div className="text-[11.5px] text-steel mt-0.5">
              {e.actor_email || e.actor_id || "system"} · {e.actor_role || "—"}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Small display components ─────────────────────────────────────── */

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-coach-silver/50 last:border-0">
    <div className="text-[11px] uppercase tracking-wide text-steel">{k}</div>
    <div className="text-[13px] text-carbon">{v}</div>
  </div>
);

// RC4 Part 2 · ITEM 4 — passport status derived on the API side and
// echoed here. Colored pill matches the RC1 credential-status palette.
function PassportStatusBadge({ status, exp }) {
  const map = {
    expired:        ["Expired",        "bg-critical-red text-white border-critical-red"],
    expiring_soon:  ["Expiring Soon",  "bg-amber-hold/20 text-carbon border-amber-hold"],
    valid:          ["Valid",          "bg-touring-lime/20 text-carbon border-touring-lime"],
    unknown:        ["Unknown",        "bg-white text-steel border-coach-silver"],
    "n/a":          ["N/A",            "bg-white text-steel border-coach-silver"],
  };
  const [label, cls] = map[status] || map["n/a"];
  return (
    <span className={`inline-block text-[10.5px] uppercase tracking-widest px-1.5 py-[1px] rounded-sm border ${cls}`}>
      {label}{exp && status !== "n/a" ? ` · ${exp}` : ""}
    </span>
  );
}


// RC10b Item 1 — DQ-derived status pill. Replaces the deprecated
// `driver.mvr_status` / `driver.drug_alcohol_program_status` renderers.
// Statuses: Complete | Needs Attention | Not Started.
function DqStatusPill({ s, testid }) {
  const map = {
    "Complete":        "bg-touring-lime/20 text-carbon border-touring-lime",
    "Needs Attention": "bg-amber-hold/20 text-carbon border-amber-hold",
    "Not Started":     "bg-white text-steel border-coach-silver",
  };
  const label = s || "Not Started";
  const cls = map[label] || map["Not Started"];
  return (
    <span
      data-testid={testid}
      className={`inline-block text-[10.5px] uppercase tracking-widest px-1.5 py-[1px] rounded-sm border ${cls}`}
    >
      {label}
    </span>
  );
}


const ComplianceItem = ({ label, exp, level, extra }) => (
  <div className="p-3 bg-road-white border border-coach-silver rounded-sm">
    <div className="text-[10.5px] uppercase tracking-widest text-steel">{label}</div>
    <div className="mt-1"><CredentialChip level={level || "unknown"} label={exp || "—"} /></div>
    {extra && <div className="text-[11px] text-steel mt-2">{extra}</div>}
  </div>
);

/* ── RC5 Item 4 · DQ File section ──────────────────────────────────
   Driver Qualification File rollup. Lists driver's credential status
   summary + attached compliance documents. Reuses existing
   `document_type` enum values (cdl / medical_card / mvr / insurance /
   coi / other) — no new enum values are added. */
function DQFileSection({ driver, onOpenDocuments }) {
  const [docs, setDocs] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    documentApi
      .list({ entity_type: "driver", entity_id: driver.id, limit: 200 })
      .then((rows) => {
        if (!alive) return;
        const dq_types = new Set(["cdl", "medical_card", "mvr", "insurance", "coi"]);
        const filtered = (rows || [])
          .filter((r) => !r.is_archived && dq_types.has(r.document_type))
          .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        setDocs(filtered);
      })
      .catch(() => setDocs([]));
    return () => { alive = false; };
  }, [driver.id]);

  const summary = React.useMemo(() => {
    const bits = [];
    const cs = driver.credential_status || {};
    const chip = (label, lvl) => {
      if (!lvl || lvl === "unknown") return null;
      return `${label} ${lvl.replace(/_/g, " ")}`;
    };
    const c1 = chip("CDL", cs.cdl); if (c1) bits.push(c1);
    const c2 = chip("Medical card", cs.medical_card); if (c2) bits.push(c2);
    // RC10b Item 1 — summary now reads from DQ-derived status.
    const dqMvr = driver.dq_derived?.mvr;
    if (dqMvr) bits.push(`MVR ${dqMvr}`);
    const dqDa = driver.dq_derived?.drug_alcohol;
    if (dqDa) bits.push(`Drug/Alcohol ${dqDa}`);
    return bits.join(" · ") || "No credential status recorded yet.";
  }, [driver]);

  return (
    <div className="bg-white border border-coach-silver rounded-md p-4 mb-4" data-testid="driver-section-dq-file">
      <div className="flex items-center justify-between">
        <SectionHeader testid="driver-dq-file-header">
          <span className="inline-flex items-center gap-1.5"><FileCheck size={13} /> DQ File</span>
        </SectionHeader>
        {/* RC7 Item 1 — upload affordance removed. The DQ File
            Checklist is administrative-only (RC6) and this adjacent
            rollup no longer offers a scoped shortcut. Ops can still
            upload standard documents via the Documents tab or the
            Document Center. */}
      </div>
      <div className="grid md:grid-cols-4 gap-3 text-[12.5px] mb-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-steel">CDL</div>
          <div className="mt-1 text-carbon">
            {driver.cdl_number
              ? <span className="font-mono text-[12px]">{driver.cdl_class || ""} · {driver.cdl_state || ""}</span>
              : <span className="text-steel">—</span>}
          </div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-steel">Medical card exp.</div>
          <div className="mt-1 text-carbon">{driver.medical_card_expiration || <span className="text-steel">—</span>}</div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-steel">MVR</div>
          <div className="mt-1 text-carbon" data-testid="driver-dq-file-mvr">
            <DqStatusPill s={driver.dq_derived?.mvr} />
          </div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-steel">Drug / Alcohol</div>
          <div className="mt-1 text-carbon" data-testid="driver-dq-file-da">
            <DqStatusPill s={driver.dq_derived?.drug_alcohol} />
          </div>
        </div>
      </div>
      <div className="text-[11.5px] text-steel mb-3" data-testid="driver-dq-status-summary">{summary}</div>
      <div className="border-t border-coach-silver/60 pt-3">
        <div className="text-[10.5px] uppercase tracking-widest text-steel mb-2">
          DQ Documents on file
        </div>
        {docs === null && (
          <div className="text-[12px] text-steel">Loading…</div>
        )}
        {docs !== null && docs.length === 0 && (
          <div className="text-[12px] text-steel" data-testid="driver-dq-empty">
            No DQ documents on file yet.
          </div>
        )}
        {docs !== null && docs.length > 0 && (
          <ul className="text-[12.5px] space-y-1" data-testid="driver-dq-list">
            {docs.slice(0, 10).map((d) => (
              <li key={d.id} className="flex items-center justify-between border-b border-coach-silver/40 last:border-0 py-1">
                <span className="text-carbon">{d.title}</span>
                <span className="text-steel uppercase text-[10.5px] tracking-widest">
                  {d.document_type.replace(/_/g, " ")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
// RC10j — DOB formatter (MM/DD/YYYY) with graceful fallback. Never
// throws; never console-logs the value.
function _formatDobMMDDYYYY(iso) {
  if (!iso || typeof iso !== "string") return "—";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[2]}/${m[3]}/${m[1]}`;
}

